import * as React from 'react';
import * as Sentry from '@sentry/react';
import { Route } from 'react-router-dom';
import { Integrations } from '@sentry/tracing';
import { useTranslation } from 'react-i18next';

import { APP_ENV, APP_ID } from '../../../../shared/config/constants';
import { SENTRY_DSN } from '../../config/constants';
import HomeScreen from '../../screens/Home';
import AdjustUsageScreen from '../../screens/AdjustUsage';
import DeliveryDetailsForm from '../../screens/DeliveryDetailsForm';
import PaymentDetailsForm from '../../screens/PaymentDetailsForm';
import ConfirmScreen from '../../screens/Confirm';
import useRegistrationDeskMultiStepForm from '../../hooks/useRegistrationDeskMultistepForm';
import RegistrationDeskStep from '../RegistrationDeskStep';
import PersonalDetailsForm from '../../screens/PersonalDetailsForm';
import ResetJourneyConfirmationDialog from '../../components/ResetJourneyConfirmationDialog';
import PricingStructure from '../../screens/PricingStructure';
import EstimateExplanationScreen from '../../screens/EstimateExplanation';
import gtm from '../../../../shared/helpers/gtm';

if (APP_ENV !== 'development' && APP_ENV !== 'ci') {
  Sentry.init({
    dsn: String(SENTRY_DSN),
    integrations: [new Integrations.BrowserTracing()],
    attachStacktrace: true,
    tracesSampleRate: 1.0,
    environment: APP_ENV,
    allowUrls: [/^https?:\/\/[^/]+\/libraries\/hvc_widgets\//],
  });
}

const RegistrationDesk = () => {
  const { t } = useTranslation();

  const [snapshot, send] = useRegistrationDeskMultiStepForm();

  const onCloseButtonClick = () => {
    send({
      type: 'reset.closeJourney',
    });
  };

  return (
    <div>
      <ResetJourneyConfirmationDialog
        state={snapshot.value.dialog}
        onConfirm={() => {
          send({
            type: 'reset',
          });
          gtm.trackUserEvent(String(APP_ID), 'Reset journey', snapshot.value.form);
        }}
        onCancel={() => {
          send({
            type: 'reset.cancel',
          });
        }}
      />

      <Route
        path="*"
        render={() => {
          if (snapshot.matches({
            form: 'enteringRegistrationType',
          })) {
            return (
              <HomeScreen
                onAdressSelected={(payload) => {
                  send({
                    type: 'proceed.saveRegistrationType',
                    ...payload,
                  });
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'enteringUsage',
          })) {
            return (
              <RegistrationDeskStep
                currentStep={snapshot.value}
                component={AdjustUsageScreen}
                onCloseButtonClick={onCloseButtonClick}
                componentProps={{
                  context: snapshot.context,
                  onOpenPricingStructure: () => {
                    send({
                      type: 'info.viewPricingStructure',
                    });
                    gtm.trackUserEvent(String(APP_ID), 'Open pricing structure', t('registration_desk.buttons.explanation'));
                  },
                  onOpenEstimateExplanation: () => {
                    send({
                      type: 'info.viewEstimateExplanation',
                    });
                    gtm.trackUserEvent(String(APP_ID), 'Open estimate explanation', t('registration_desk.screens.estimate_explanation.title'));
                  },
                  onAddressChange: (payload) => {
                    send({
                      type: 'reset.changeDeliveryAddress',
                      ...payload,
                    });
                  },
                  onUsageUpdate: (payload) => {
                    send({
                      type: 'proceed.updateUsage',
                      ...payload,
                    });
                  },
                  onProceed: (payload) => {
                    send({
                      type: 'proceed.saveUsage',
                      ...payload,
                    });
                  },
                  onOpenCustomUsageAccordion: () => {
                    send({
                      type: 'openCustomUsageAccordion',
                    });
                  },
                  onCloseCustomUsageAccordion: () => {
                    send({
                      type: 'closeCustomUsageAccordion',
                    });
                  },
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'viewingPricingStructure',
          })) {
            return (
              <RegistrationDeskStep
                onCloseButtonClick={onCloseButtonClick}
                component={PricingStructure}
                hideJourneyOverlay
                componentProps={{
                  context: snapshot.context,
                  onProceed: () => {
                    send({
                      type: 'goBack',
                    });
                  },
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'viewingEstimateExplanation',
          })) {
            return (
              <RegistrationDeskStep
                onCloseButtonClick={onCloseButtonClick}
                component={EstimateExplanationScreen}
                hideJourneyOverlay
                componentProps={{
                  context: snapshot.context,
                  onProceed: () => {
                    send({
                      type: 'goBack',
                    });
                  },
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'enteringPersonalDetails',
          })) {
            return (
              <RegistrationDeskStep
                currentStep={snapshot.value}
                onCloseButtonClick={onCloseButtonClick}
                component={PersonalDetailsForm}
                componentProps={{
                  context: snapshot.context,
                  onGoBack: (payload) => {
                    send({
                      type: 'goBack',
                      ...payload,
                    });
                  },
                  onProceed: (payload) => {
                    send({
                      type: 'proceed.savePersonalDetails',
                      ...payload,
                    });
                  },
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'enteringDeliveryDetails',
          })) {
            return (
              <RegistrationDeskStep
                currentStep={snapshot.value}
                onCloseButtonClick={onCloseButtonClick}
                component={DeliveryDetailsForm}
                componentProps={{
                  context: snapshot.context,
                  onAddressChange: (payload) => {
                    send({
                      type: 'reset.changeDeliveryAddress',
                      ...payload,
                    });
                  },
                  onGoBack: (payload) => {
                    send({
                      type: 'goBack',
                      ...payload,
                    });
                  },
                  onProceed: (payload) => {
                    send({
                      type: 'proceed.saveDeliveryDetails',
                      ...payload,
                    });
                  },
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'enteringPaymentDetails',
          })) {
            return (
              <RegistrationDeskStep
                currentStep={snapshot.value}
                onCloseButtonClick={onCloseButtonClick}
                component={PaymentDetailsForm}
                componentProps={{
                  context: snapshot.context,
                  onGoBack: (payload) => {
                    send({
                      type: 'goBack',
                      ...payload,
                    });
                  },
                  onProceed: (payload) => {
                    send({
                      type: 'proceed.savePaymentDetails',
                      ...payload,
                    });
                  },
                }}
              />
            );
          }

          if (snapshot.matches({
            form: 'reviewingTermsAndConditions',
          })) {
            return (
              <>
                <RegistrationDeskStep
                  currentStep={snapshot.value}
                  onCloseButtonClick={onCloseButtonClick}
                  component={ConfirmScreen}
                  componentProps={{
                    snapshot,
                    send,
                    context: snapshot.context,
                    onGoBack: () => {
                      send({
                        type: 'goBack',
                      });
                    },
                    onProceed: () => {
                      send({
                        type: 'proceed.acceptTermsAndConditions',
                      });
                    },
                  }}
                />
              </>
            );
          }
          return null;
        }}
      />
    </div>
  );
};

export default RegistrationDesk;
